import { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Scale, 
  ChevronDown, 
  ChevronUp, 
  Loader2, 
  Plus,
  MessageSquare,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn, getAvatarColor } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { 
  useSessionEscalations, 
  ESCALATION_STATUS_CONFIG,
} from '@/hooks/useSessionEscalations';
import { EscalateModal } from './EscalateModal';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useFilteredTasks } from '@/hooks/useFilteredTasks';
import { TaskDetailSheet } from '@/components/tasks/TaskDetailSheet';
import type { SessionEscalationWithUsers, EscalationStatus } from '@/types/support.types';
import type { FilteredTask } from '@/hooks/useFilteredTasks';

interface EscalationPanelProps {
  sessionId: string | null;
  className?: string;
}

export function EscalationPanel({ sessionId, className }: EscalationPanelProps) {
  const { data: currentAppUser } = useCurrentAppUser();
  const {
    escalations,
    isLoading,
    respondEscalation,
    isResponding,
    cancelEscalation,
    isCancelling,
  } = useSessionEscalations(sessionId);

  const { users: appUsers } = useAppUsers();
  const { updateTask, deleteTask } = useFilteredTasks();

  const [isOpen, setIsOpen] = useState(false);
  const [escalateModalOpen, setEscalateModalOpen] = useState(false);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Fetch selected task for the detail sheet
  const { data: selectedTask } = useQuery({
    queryKey: ['task-detail', selectedTaskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', selectedTaskId!)
        .single();
      if (error) throw error;
      return data as unknown as FilteredTask;
    },
    enabled: !!selectedTaskId,
  });

  // Form states
  const [responseText, setResponseText] = useState('');

  const pendingCount = escalations.filter(e => e.status === 'pending').length;

  const getUserName = (user: SessionEscalationWithUsers['escalated_by_user']) => {
    if (!user) return 'Usuário desconhecido';
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sem nome';
  };

  const handleRespond = async (escalationId: string) => {
    if (!responseText.trim()) {
      toast.error('Digite uma resposta');
      return;
    }

    try {
      await respondEscalation({
        escalation_id: escalationId,
        response: responseText,
      });
      toast.success('Resposta enviada');
      setRespondingId(null);
      setResponseText('');
    } catch (err) {
      console.error('[EscalationPanel] Erro ao responder escalação:', err);
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      toast.error(`Erro ao responder: ${message}`);
    }
  };

  const handleCancel = async (escalationId: string) => {
    if (!cancelReason.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }

    try {
      // Find the escalation to get task_id
      const escalation = escalations.find(e => e.id === escalationId);
      
      await cancelEscalation({ escalation_id: escalationId, reason: cancelReason.trim() });
      
      // Delete associated task if exists
      if (escalation?.task_id) {
        try {
          await deleteTask.mutateAsync(escalation.task_id);
        } catch {
          // Don't block cancellation if task deletion fails
        }
      }

      toast.success('Escalação cancelada');
      setCancellingId(null);
      setCancelReason('');
    } catch (err) {
      console.error('[EscalationPanel] Erro ao cancelar escalação:', err);
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      toast.error(`Erro ao cancelar: ${message}`);
    }
  };

  const canRespond = (escalation: SessionEscalationWithUsers) => {
    return escalation.status === 'pending' && escalation.escalated_to === currentAppUser?.id;
  };

  const canCancel = (escalation: SessionEscalationWithUsers) => {
    return escalation.status === 'pending' && escalation.escalated_by === currentAppUser?.id;
  };

  if (!sessionId) return null;

  return (
    <div className={cn("border-t bg-card", className)}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full flex items-center justify-between px-4 py-3 h-auto rounded-none"
          >
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              <span className="font-medium text-sm">Escalações</span>
              {pendingCount > 0 && (
                <Badge variant="destructive" className="px-1.5 py-0.5 text-xs">
                  {pendingCount}
                </Badge>
              )}
            </div>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {escalations.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma escalação neste chamado
                  </p>
                )}

                <div className={cn(escalations.length > 0 && "max-h-[400px] overflow-y-auto pr-1 space-y-3")}>
                {escalations.map((escalation) => (
                  <EscalationCard
                    key={escalation.id}
                    escalation={escalation}
                    canRespond={canRespond(escalation)}
                    canCancel={canCancel(escalation)}
                    isResponding={respondingId === escalation.id}
                    isCancelConfirming={cancellingId === escalation.id}
                    responseText={respondingId === escalation.id ? responseText : ''}
                    onResponseChange={(text) => setResponseText(text)}
                    onStartResponding={() => {
                      setRespondingId(escalation.id);
                      setResponseText('');
                    }}
                    onCancelResponding={() => {
                      setRespondingId(null);
                      setResponseText('');
                    }}
                    onSubmitResponse={() => handleRespond(escalation.id)}
                    isSubmitting={isResponding && respondingId === escalation.id}
                    onStartCancelConfirm={() => { setCancellingId(escalation.id); setCancelReason(''); }}
                    onAbortCancel={() => { setCancellingId(null); setCancelReason(''); }}
                    onConfirmCancel={() => handleCancel(escalation.id)}
                    isCancelSubmitting={isCancelling && cancellingId === escalation.id}
                    cancelReason={cancellingId === escalation.id ? cancelReason : ''}
                    onCancelReasonChange={(text) => setCancelReason(text)}
                    getUserName={getUserName}
                    onViewTask={(taskId) => setSelectedTaskId(taskId)}
                  />
                ))}
                </div>


                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setEscalateModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Nova Escalação
                </Button>

                <EscalateModal
                  sessionId={sessionId}
                  open={escalateModalOpen}
                  onOpenChange={setEscalateModalOpen}
                />
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <TaskDetailSheet
        task={selectedTask || null}
        open={!!selectedTaskId}
        onOpenChange={(open) => { if (!open) setSelectedTaskId(null); }}
        users={appUsers.map(u => ({ id: u.id, display_name: u.display_name, email: u.email }))}
        onUpdate={async (id, data) => { await updateTask.mutateAsync({ id, ...data }); }}
        onDelete={async (id) => { await deleteTask.mutateAsync(id); setSelectedTaskId(null); }}
        onComplete={(task) => {
          updateTask.mutate({ id: task.id, status: 'completed', completed_at: new Date().toISOString() });
        }}
        isUpdating={updateTask.isPending}
        isDeleting={deleteTask.isPending}
      />
    </div>
  );
}

// Escalation card sub-component
interface EscalationCardProps {
  escalation: SessionEscalationWithUsers;
  canRespond: boolean;
  canCancel: boolean;
  isResponding: boolean;
  isCancelConfirming: boolean;
  responseText: string;
  onResponseChange: (text: string) => void;
  onStartResponding: () => void;
  onCancelResponding: () => void;
  onSubmitResponse: () => void;
  isSubmitting: boolean;
  onStartCancelConfirm: () => void;
  onAbortCancel: () => void;
  onConfirmCancel: () => void;
  isCancelSubmitting: boolean;
  cancelReason: string;
  onCancelReasonChange: (text: string) => void;
  getUserName: (user: SessionEscalationWithUsers['escalated_by_user']) => string;
  onViewTask?: (taskId: string) => void;
}

function EscalationCard({
  escalation,
  canRespond,
  canCancel,
  isResponding,
  isCancelConfirming,
  responseText,
  onResponseChange,
  onStartResponding,
  onCancelResponding,
  onSubmitResponse,
  isSubmitting,
  onStartCancelConfirm,
  onAbortCancel,
  onConfirmCancel,
  isCancelSubmitting,
  cancelReason,
  onCancelReasonChange,
  getUserName,
  onViewTask,
}: EscalationCardProps) {
  const statusConfig = ESCALATION_STATUS_CONFIG[escalation.status as EscalationStatus];
  const isPending = escalation.status === 'pending';
  const isCancelled = escalation.status === 'cancelled';

  const getStatusIcon = () => {
    if (isCancelled) return <XCircle className="h-3 w-3 mr-1" />;
    if (isPending) return <Clock className="h-3 w-3 mr-1" />;
    return <CheckCircle className="h-3 w-3 mr-1" />;
  };

  const getStatusVariant = (): 'destructive' | 'secondary' | 'outline' => {
    if (isCancelled) return 'outline';
    if (isPending) return 'destructive';
    return 'secondary';
  };

  return (
    <div className={cn(
      "border rounded-lg p-3 space-y-2",
      isCancelled ? "bg-muted/20 opacity-60" : isPending ? "bg-accent/50 border-destructive/50" : "bg-muted/30"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-6 w-6 shrink-0">
            <AvatarFallback className={cn("text-xs text-white", getAvatarColor(getUserName(escalation.escalated_by_user)))}>
              {getUserName(escalation.escalated_by_user).charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="text-xs truncate">
            <span className="font-medium">{getUserName(escalation.escalated_by_user)}</span>
            <span className="text-muted-foreground"> → </span>
            <span className="font-medium">{getUserName(escalation.escalated_to_user)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {escalation.task_id && onViewTask && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onViewTask(escalation.task_id!)}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Ver Tarefa
            </Button>
          )}
          <Badge 
            variant={getStatusVariant()} 
            className="text-xs px-1.5 py-0"
          >
            {getStatusIcon()}
            {statusConfig?.label || escalation.status}
          </Badge>
        </div>
      </div>

      {/* Question */}
      <div className="text-sm">
        <p className="text-muted-foreground text-xs mb-1">Pergunta:</p>
        <p className="whitespace-pre-wrap">{escalation.question}</p>
      </div>

      {/* Response (if exists) */}
      {escalation.response && (
        <div className="text-sm border-t pt-2 mt-2">
          <p className="text-muted-foreground text-xs mb-1 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {isCancelled ? 'Motivo do cancelamento:' : 'Resposta:'}
          </p>
          <p className="whitespace-pre-wrap">{escalation.response}</p>
          {escalation.responded_at && !isCancelled && (
            <p className="text-xs text-muted-foreground mt-1">
              {safeFormat(new Date(escalation.responded_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          )}
        </div>
      )}

      {/* Respond button (if can respond and not cancelled) */}
      {canRespond && !isResponding && !isCancelled && (
        <Button size="sm" variant="outline" className="w-full" onClick={onStartResponding}>
          <MessageSquare className="h-4 w-4 mr-1" />
          Responder
        </Button>
      )}

      {isResponding && (
        <div className="space-y-2 border-t pt-2">
          <Textarea
            value={responseText}
            onChange={(e) => onResponseChange(e.target.value)}
            placeholder="Digite sua resposta..."
            rows={3}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancelResponding}>
              Cancelar
            </Button>
            <Button 
              size="sm" 
              onClick={onSubmitResponse}
              disabled={isSubmitting || !responseText.trim()}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Enviar Resposta
            </Button>
          </div>
        </div>
      )}

      {/* Cancel escalation button (if creator and still pending) */}
      {canCancel && !isCancelConfirming && !isResponding && (
        <Button size="sm" variant="ghost" className="w-full text-destructive hover:text-destructive" onClick={onStartCancelConfirm}>
          <XCircle className="h-4 w-4 mr-1" />
          Cancelar Escalação
        </Button>
      )}

      {isCancelConfirming && (
        <div className="space-y-2 border-t pt-2">
          <p className="text-sm text-muted-foreground">Informe o motivo do cancelamento:</p>
          <Textarea
            value={cancelReason}
            onChange={(e) => onCancelReasonChange(e.target.value)}
            placeholder="Informe o motivo do cancelamento..."
            rows={2}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onAbortCancel}>
              Não
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={onConfirmCancel}
              disabled={isCancelSubmitting || !cancelReason.trim()}
            >
              {isCancelSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Sim, Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Timestamp */}
      <p className="text-xs text-muted-foreground">
        {formatDistanceToNow(new Date(escalation.escalated_at), { addSuffix: true, locale: ptBR })}
      </p>
    </div>
  );
}
